import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Clock, Search, Store, LayoutList, LayoutGrid, RefreshCw, Edit2, X, ChevronDown, MapPin, Tag, Package, AlertCircle, CheckCircle2, ArrowLeft, ImagePlus, EyeOff, Camera, Mail, Phone, Lock } from 'lucide-react';
import { format, differenceInDays, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { annonceAPI } from '../services/api';
import Toast from '../components/Toast';

const inp = { width:'100%', padding:'8px 11px', border:'1px solid #D1D5DB', borderRadius:7, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', color:'#111827', backgroundColor:'white' };
const lbl = { display:'block', fontSize:'0.75rem', fontWeight:700, color:'#374151', marginBottom:5, letterSpacing:'0.02em' };

const TYPE_STYLES = {
  Vente:     { bg:'#DBEAFE', c:'#1D4ED8', border:'#BFDBFE' },
  Recherche: { bg:'#EDE9FE', c:'#6D28D9', border:'#DDD6FE' },
  'À donner':{ bg:'#D1FAE5', c:'#065F46', border:'#A7F3D0' },
};
const STATUS_STYLES = {
  pending:   { bg:'#FEF9C3', c:'#713F12', label:'En attente' },
  published: { bg:'#D1FAE5', c:'#065F46', label:'Publiée' },
  rejected:  { bg:'#FEE2E2', c:'#991B1B', label:'Refusée' },
  draft:     { bg:'#F3F4F6', c:'#6B7280', label:'Brouillon' },
};

const REFS_DEFAULT = { categorie: [], marque: [], etat: [], devise: ['CHF', 'EUR', 'USD'] };

const API_BASE = process.env.REACT_APP_API_URL?.replace('/v2/api', '') || 'http://localhost:3001';

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
};

const AnnonceImage = ({ type, seed, size = 'grid', images }) => {
  const realImgs = Array.isArray(images) ? images : (images ? (() => { try { return JSON.parse(images); } catch { return []; } })() : []);
  const h = size === 'grid' ? 140 : 68;
  const w = size === 'grid' ? '100%' : 68;
  if (realImgs.length > 0) {
    return (
      <div style={{width:w, height:h, flexShrink:0, borderRadius: size==='grid' ? '8px 8px 0 0' : 8, overflow:'hidden', backgroundColor:'#F3F4F6', position:'relative'}}>
        <img src={`${API_BASE}${realImgs[0]}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
        {size === 'grid' && <div style={{position:'absolute',inset:0,background:'linear-gradient(to bottom,transparent 50%,rgba(0,0,0,0.22) 100%)'}}/>}
        {size === 'grid' && realImgs.length > 1 && <span style={{position:'absolute',bottom:6,right:8,backgroundColor:'rgba(0,0,0,0.5)',color:'white',fontSize:'0.625rem',fontWeight:700,padding:'2px 6px',borderRadius:4}}>{realImgs.length} photos</span>}
      </div>
    );
  }
  const seeds = { Vente:[1005,1006,1010,1012,1015], Recherche:[1035,1038,1042,1047], 'À donner':[1055,1060,1065,1070] };
  const s = (seeds[type] || seeds.Vente)[(seed||0) % 5];
  return (
    <div style={{width:w, height:h, flexShrink:0, borderRadius: size==='grid' ? '8px 8px 0 0' : 8, overflow:'hidden', backgroundColor:'#F3F4F6', position:'relative'}}>
      <img src={`https://picsum.photos/seed/${s}/${size==='grid'?400:160}/${h}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
      {size === 'grid' && <div style={{position:'absolute',inset:0,background:'linear-gradient(to bottom,transparent 50%,rgba(0,0,0,0.22) 100%)'}}/>}
    </div>
  );
};

const EMPTY_FORM = { titre:'', contenu:'', type:'Vente', categorie:'', marque:'', modele:'', prix:'', devise:'CHF', etat:'', localisation:'', duree_mois:1, contact_email:'', contact_telephone:'' };

const daysLeft = (expires_at) => {
  if (!expires_at) return null;
  return differenceInDays(new Date(expires_at), new Date());
};

const AnnoncesPage = () => {
  const { isAuthenticated, user, isComite } = useAuth();
  const [annonces, setAnnonces] = useState([]);
  const [myAnnonces, setMyAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filterType, setFilterType] = useState('Tout');
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [refs, setRefs] = useState(REFS_DEFAULT);
  const [newPhotos, setNewPhotos] = useState([]); // File[]
  const [keptPhotos, setKeptPhotos] = useState([]); // URLs existantes à conserver
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [renewModal, setRenewModal] = useState(null); // { id }
  const [renewDuree, setRenewDuree] = useState(1);
  const [showMyAnnonces, setShowMyAnnonces] = useState(false);
  const [showLoginBanner, setShowLoginBanner] = useState(false);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const fileInputRef = useRef(null);
  const isMobile = useWindowWidth() < 700;
  const location = useLocation();
  const navigate = useNavigate();

  const openDetail = async (id) => {
    setSelectedLoading(true);
    try {
      const r = await annonceAPI.getOne(id);
      const d = r.data;
      if (d.images && typeof d.images === 'string') {
        try { d.images = JSON.parse(d.images); } catch { d.images = []; }
      }
      setSelected(d);
    } catch { showToast('Erreur de chargement', 'error'); }
    finally { setSelectedLoading(false); }
  };

  const showToast = (msg, type='success') => setToast({ message:msg, type });

  const loadAnnonces = useCallback(async () => {
    try {
      const [resAnnonces, resRefs] = await Promise.all([
        annonceAPI.getAll(),
        annonceAPI.getRefs(),
      ]);
      setAnnonces(resAnnonces.data);
      setRefs(resRefs.data);
      if (isAuthenticated) { const r = await annonceAPI.getMy(); setMyAnnonces(r.data); }
    } catch { showToast('Erreur de chargement', 'error'); }
    finally { setLoading(false); }
  }, [isAuthenticated]); // eslint-disable-line

  useEffect(() => { loadAnnonces(); }, [loadAnnonces]);

  useEffect(() => {
    const isOpen = showForm || !!confirm || !!renewModal || showLoginBanner;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showForm, confirm, renewModal, showLoginBanner]);

  useEffect(() => {
    if (location.state?.openAnnonceId) {
      openDetail(location.state.openAnnonceId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  const openCreate = () => {
    setEditTarget(null); setForm(EMPTY_FORM); setNewPhotos([]); setKeptPhotos([]);
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditTarget(a);
    setForm({ titre:a.titre, contenu:a.contenu, type:a.type||'Vente', categorie:a.categorie||'', marque:a.marque||'', modele:a.modele||'', prix:a.prix||'', devise:a.devise||'CHF', etat:a.etat||'', localisation:a.localisation||'', duree_mois:a.duree_mois||1, contact_email:a.contact_email||'', contact_telephone:a.contact_telephone||'' });
    const existing = Array.isArray(a.images) ? a.images : (a.images ? JSON.parse(a.images) : []);
    setKeptPhotos(existing);
    setNewPhotos([]);
    setShowForm(true);
    setSelected(null);
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const total = keptPhotos.length + newPhotos.length + files.length;
    if (total > 3) { showToast('Maximum 3 photos par annonce.', 'error'); return; }
    setNewPhotos(prev => [...prev, ...files].slice(0, 3 - keptPhotos.length));
    e.target.value = '';
  };
  const removeKept = (url) => setKeptPhotos(prev => prev.filter(u => u !== url));
  const removeNew = (idx) => setNewPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation : au moins un moyen de contact requis
    if (!form.contact_email && !form.contact_telephone) {
      showToast('Veuillez fournir au moins un moyen de contact (email ou téléphone)', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const fd = new FormData();
      const formToSend = { ...form };
      if (form.marque === 'Andere / Autre' && form.marqueLibre) formToSend.marque = form.marqueLibre;
      delete formToSend.marqueLibre;
      Object.entries(formToSend).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) fd.append(k, v); });
      newPhotos.forEach(f => fd.append('images', f));
      keptPhotos.forEach(u => fd.append('keep_images', u));
      if (editTarget) {
        await annonceAPI.update(editTarget.id, fd);
        showToast('Annonce modifiée, resoumise à validation.');
      } else {
        await annonceAPI.create(fd);
        showToast('Annonce soumise — elle sera publiée après validation du comité.');
      }
      setShowForm(false); setEditTarget(null); setForm(EMPTY_FORM); setNewPhotos([]); setKeptPhotos([]);
      loadAnnonces();
    } catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = (id) => setConfirm({ message:'Supprimer définitivement cette annonce ?', onConfirm: async () => {
    try { await annonceAPI.delete(id); showToast('Annonce supprimée.'); setSelected(null); loadAnnonces(); }
    catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  }});

  const handleWithdraw = (id) => setConfirm({ message:'Retirer cette annonce ? Elle ne sera plus visible mais vous pourrez la republier.', onConfirm: async () => {
    try { await annonceAPI.withdraw(id); showToast('Annonce retirée.'); setSelected(null); loadAnnonces(); }
    catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  }, confirmLabel:'Retirer', confirmColor:'#D97706' });

  const handleRenewConfirm = async () => {
    if (!renewModal) return;
    try { await annonceAPI.renew(renewModal.id, renewDuree); showToast(`Demande de prolongation envoyée au comité.`); setRenewModal(null); loadAnnonces(); }
    catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  };

  const photoCount = keptPhotos.length + newPhotos.length;

  const filtered = annonces.filter(a =>
    (filterType === 'Tout' || a.type === filterType) &&
    (!filterCat || a.categorie === filterCat) &&
    (!search || a.titre.toLowerCase().includes(search.toLowerCase()) ||
      (a.marque||'').toLowerCase().includes(search.toLowerCase()) ||
      (a.modele||'').toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300}}>
      <div style={{width:24,height:24,border:'2px solid #111827',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  // ── Vue détail ──
  if (selectedLoading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300,pointerEvents:'none'}}>
      <div style={{width:24,height:24,border:'2px solid #111827',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  if (selected) {
    const ts = TYPE_STYLES[selected.type] || TYPE_STYLES.Vente;
    const dl = daysLeft(selected.expires_at);
    const isOwner = user && (Number(selected.auteur_id) === Number(user.id) || isComite);
    const imgs = Array.isArray(selected.images) ? selected.images : [];

    return (
      <div style={{display:'flex',flexDirection:'column',gap:12,maxWidth:1100,margin:'0 auto',width:'100%'}}>
        <button onClick={()=>setSelected(null)} style={{display:'inline-flex',alignItems:'center',gap:6,background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:'0.8125rem',padding:0,fontWeight:500,alignSelf:'flex-start'}}>
          <ArrowLeft style={{width:14,height:14}}/> Retour aux annonces
        </button>

        <div style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,0.07)',display:isMobile?'flex':'grid',flexDirection:'column',gridTemplateColumns:isMobile?undefined:'1fr 1fr'}}>

          {/* ── Colonne gauche : photos ── */}
          <div style={{backgroundColor:'#F3F4F6',minHeight:isMobile?200:440,display:'flex',flexDirection:'column',gap:3,overflow:'hidden'}}>
            {imgs.length > 0 ? (
              <>
                <div style={{flex:1,overflow:'hidden',minHeight:0}}>
                  <img src={`${API_BASE}${imgs[0]}`} alt="Photo 1" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                </div>
                {imgs.length > 1 && (
                  <div style={{display:'flex',gap:3,height:80,flexShrink:0}}>
                    {imgs.slice(1).map((url, i) => (
                      <div key={i} style={{flex:1,overflow:'hidden'}}>
                        <img src={`${API_BASE}${url}`} alt={`Photo ${i+2}`} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#D1D5DB',flexDirection:'column',gap:8}}>
                <Package style={{width:40,height:40}}/>
                <span style={{fontSize:'0.75rem'}}>Pas de photo</span>
              </div>
            )}
          </div>

          {/* ── Colonne droite : infos ── */}
          <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:12}}>

            {/* Type + état + titre + prix */}
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                <span style={{backgroundColor:ts.bg,color:ts.c,border:`1px solid ${ts.border}`,fontSize:'0.75rem',fontWeight:700,padding:'3px 10px',borderRadius:20}}>{selected.type}</span>
                {selected.etat && <span style={{backgroundColor:'#F3F4F6',color:'#374151',fontSize:'0.75rem',fontWeight:600,padding:'3px 10px',borderRadius:20}}>{selected.etat}</span>}
                {dl !== null && dl <= 14 && <span style={{backgroundColor:'#FEF9C3',color:'#713F12',fontSize:'0.75rem',fontWeight:600,padding:'3px 10px',borderRadius:20,display:'inline-flex',alignItems:'center',gap:3}}><AlertCircle style={{width:11,height:11}}/>{dl <= 0 ? 'Expirée' : `Expire dans ${dl}j`}</span>}
              </div>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10}}>
                <div>
                  <h1 style={{fontSize:'1.25rem',fontWeight:800,color:'#111827',margin:0,lineHeight:1.2}}>{selected.titre}</h1>
                  {(selected.marque || selected.modele) && <div style={{fontSize:'0.875rem',color:'#6B7280',marginTop:3}}>{[selected.marque,selected.modele].filter(Boolean).join(' · ')}</div>}
                </div>
                {selected.prix && <div style={{flexShrink:0,textAlign:'right'}}><div style={{fontSize:'1.5rem',fontWeight:900,color:'#111827',lineHeight:1}}>{Number(selected.prix).toLocaleString('fr-CH')}</div><div style={{fontSize:'0.8125rem',fontWeight:600,color:'#6B7280'}}>{selected.devise||'CHF'}</div></div>}
              </div>
            </div>

            {/* Méta infos */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              {selected.categorie && <div style={{backgroundColor:'#F9FAFB',borderRadius:7,padding:'6px 10px'}}><div style={{fontSize:'0.55rem',color:'#9CA3AF',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Catégorie</div><div style={{fontSize:'0.75rem',fontWeight:600,color:'#111827',display:'flex',alignItems:'flex-start',gap:4,wordBreak:'break-word'}}><Tag style={{width:10,height:10,color:'#9CA3AF',flexShrink:0,marginTop:2}}/>{selected.categorie}</div></div>}
              {selected.localisation && <div style={{backgroundColor:'#F9FAFB',borderRadius:7,padding:'6px 10px'}}><div style={{fontSize:'0.55rem',color:'#9CA3AF',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Localisation</div><div style={{fontSize:'0.75rem',fontWeight:600,color:'#111827',display:'flex',alignItems:'center',gap:4}}><MapPin style={{width:10,height:10,color:'#9CA3AF',flexShrink:0}}/>{selected.localisation}</div></div>}
              <div style={{backgroundColor:'#F9FAFB',borderRadius:7,padding:'6px 10px'}}><div style={{fontSize:'0.55rem',color:'#9CA3AF',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Vendeur</div><div style={{fontSize:'0.75rem',fontWeight:700,color:'#111827',fontFamily:'monospace'}}>{selected.auteur_indicatif}</div><div style={{fontSize:'0.6875rem',color:'#9CA3AF'}}>{selected.auteur_prenom} {selected.auteur_nom}</div>{selected.auteur_telephone && <div style={{fontSize:'0.6875rem',color:'#374151',marginTop:2,display:'flex',alignItems:'center',gap:4}}><Phone style={{width:10,height:10,color:'#9CA3AF'}}/><a href={`tel:${selected.auteur_telephone}`} style={{color:'#374151',textDecoration:'none'}}>{selected.auteur_telephone}</a></div>}</div>
              {selected.expires_at && <div style={{backgroundColor:dl!==null&&dl<=7?'#FEF2F2':dl!==null&&dl<=14?'#FFFBEB':'#F9FAFB',borderRadius:7,padding:'6px 10px'}}><div style={{fontSize:'0.55rem',color:'#9CA3AF',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Validité</div><div style={{fontSize:'0.75rem',fontWeight:600,color:dl!==null&&dl<=7?'#DC2626':dl!==null&&dl<=14?'#D97706':'#111827'}}>Jusqu'au {format(new Date(selected.expires_at),'dd MMM yyyy',{locale:fr})}</div><div style={{fontSize:'0.6rem',color:dl!==null&&dl<=7?'#DC2626':dl!==null&&dl<=14?'#D97706':'#9CA3AF'}}>{dl !== null ? (dl <= 0 ? 'Expirée' : `${dl}j restants`) : ''}</div></div>}
            </div>

            {/* Description */}
            <div style={{borderTop:'1px solid #F3F4F6',paddingTop:10}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',marginBottom:6}}>Description</div>
              <p style={{fontSize:'0.875rem',color:'#374151',lineHeight:1.65,margin:0,whiteSpace:'pre-wrap'}}>{selected.contenu}</p>
            </div>

            {/* Actions */}
            <div style={{marginTop:'auto',paddingTop:10,borderTop:'1px solid #F3F4F6',display:'flex',flexDirection:'column',gap:8}}>
              {isAuthenticated ? (() => {
                const contactEmail = selected.contact_email || selected.auteur_email;
                const contactTel = selected.contact_telephone || selected.auteur_telephone;
                return (
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <a
                      href={contactEmail ? `mailto:${contactEmail}?subject=Annonce HB9G : ${encodeURIComponent(selected.titre)}` : undefined}
                      onClick={!contactEmail ? e => { e.preventDefault(); showToast('Email du vendeur non disponible', 'error'); } : undefined}
                      style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,padding:'10px 16px',borderRadius:8,backgroundColor:'#111827',color:'white',textDecoration:'none',fontSize:'0.875rem',fontWeight:700,cursor:'pointer',minWidth:140}}>
                      <Mail style={{width:14,height:14}}/> Contacter par email
                    </a>
                    {contactTel && (
                      <a
                        href={`tel:${contactTel}`}
                        style={{flex:1,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,padding:'10px 16px',borderRadius:8,border:'1px solid #D1D5DB',backgroundColor:'white',color:'#111827',textDecoration:'none',fontSize:'0.875rem',fontWeight:700,cursor:'pointer',minWidth:140}}>
                        <Phone style={{width:14,height:14}}/> {contactTel}
                      </a>
                    )}
                  </div>
                );
              })() : (
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',backgroundColor:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8}}>
                  <Lock style={{width:14,height:14,color:'#9CA3AF',flexShrink:0}}/>
                  <span style={{fontSize:'0.8125rem',color:'#6B7280',flex:1}}>Connectez-vous pour voir les coordonnées du vendeur.</span>
                  <button onClick={()=>navigate('/auth')} style={{padding:'6px 12px',borderRadius:6,backgroundColor:'#111827',color:'white',border:'none',fontSize:'0.75rem',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>Se connecter</button>
                </div>
              )}
              {isOwner && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8}}>
                  <button onClick={()=>openEdit(selected)} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 14px',borderRadius:7,border:'1px solid #D1D5DB',background:'white',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#374151'}}><Edit2 style={{width:13,height:13}}/> Modifier</button>
                  {selected.statut==='published' && <button onClick={()=>{setRenewDuree(selected.duree_mois||1);setRenewModal({id:selected.id});}} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 14px',borderRadius:7,border:'1px solid #BBF7D0',backgroundColor:'#F0FDF4',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#15803D'}}><RefreshCw style={{width:13,height:13}}/> Prolonger</button>}
                  {selected.statut==='published' && <button onClick={()=>handleWithdraw(selected.id)} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 14px',borderRadius:7,border:'1px solid #FED7AA',backgroundColor:'#FFF7ED',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#C2410C'}}><EyeOff style={{width:13,height:13}}/> Retirer</button>}
                  <button onClick={()=>handleDelete(selected.id)} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 14px',borderRadius:7,border:'1px solid #FECACA',backgroundColor:'#FEF2F2',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#DC2626'}}><Trash2 style={{width:13,height:13}}/> Supprimer</button>
                </div>
              )}
            </div>
          </div>
        </div>
      {confirm && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setConfirm(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px 28px',maxWidth:400,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:10}}>Confirmer</div>
            <p style={{color:'#6B7280',fontSize:'0.9rem',margin:'0 0 24px',lineHeight:1.6}}>{confirm.message}</p>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setConfirm(null)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
              <button onClick={()=>{confirm.onConfirm();setConfirm(null);}} style={{padding:'8px 16px',borderRadius:7,backgroundColor:confirm.confirmColor||'#DC2626',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700}}>{confirm.confirmLabel||'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}
      {renewModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setRenewModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px 28px',maxWidth:380,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:6}}>Prolonger l'annonce</div>
            <p style={{color:'#6B7280',fontSize:'0.875rem',margin:'0 0 18px',lineHeight:1.5}}>Choisissez la durée de prolongation.</p>
            <div style={{display:'flex',gap:10,marginBottom:22}}>
              {[{v:1,label:'1 mois',desc:'30 jours'},{v:3,label:'3 mois',desc:'90 jours'}].map(({v,label,desc}) => {
                const active = renewDuree === v;
                return (
                  <button key={v} type="button" onClick={()=>setRenewDuree(v)} style={{flex:1,padding:'12px',borderRadius:8,border:`2px solid ${active?'#6366F1':'#E5E7EB'}`,backgroundColor:active?'#EEF2FF':'white',cursor:'pointer',textAlign:'left'}}>
                    <div style={{fontWeight:700,fontSize:'0.9375rem',color:active?'#4F46E5':'#374151'}}>{label}</div>
                    <div style={{fontSize:'0.75rem',color:active?'#6366F1':'#9CA3AF',marginTop:2}}>{desc}</div>
                  </button>
                );
              })}
            </div>
            <div style={{backgroundColor:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:7,padding:'9px 13px',fontSize:'0.8125rem',color:'#166534',marginBottom:20}}>
              Nouvelle expiration : <strong>{format(addMonths(new Date(), renewDuree), 'dd MMM yyyy', {locale:fr})}</strong>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setRenewModal(null)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
              <button onClick={handleRenewConfirm} style={{padding:'8px 16px',borderRadius:7,backgroundColor:'#15803D',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700,display:'flex',alignItems:'center',gap:6}}><RefreshCw style={{width:13,height:13}}/> Prolonger</button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)}/>}
      </div>
    );
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>

      {/* ── Toolbar ── */}
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <h2 style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em',margin:0}}>Annonces</h2>
            <span style={{backgroundColor:'#F3F4F6',color:'#6B7280',fontSize:'0.6875rem',fontWeight:600,padding:'1px 7px',borderRadius:9999}}>{filtered.length}</span>
          </div>
          <button onClick={isAuthenticated ? openCreate : ()=>setShowLoginBanner(true)} style={{display:'flex',alignItems:'center',gap:4,backgroundColor:'#111827',color:'white',border:'none',borderRadius:7,padding:'5px 10px',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',flexShrink:0}}>
            <Plus style={{width:12,height:12}}/> Déposer
          </button>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <div style={{position:'relative',flex:'1 1 280px',minWidth:200,maxWidth:400}}>
            <Search style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',width:13,height:13,color:'#9CA3AF'}}/>
            <input style={{...inp,paddingLeft:28,width:'100%',padding:'5px 8px 5px 26px',fontSize:'0.8125rem',boxSizing:'border-box'}} placeholder="Marque, modèle, titre…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div style={{display:'flex',gap:2,backgroundColor:'#F3F4F6',borderRadius:6,padding:2}}>
            {['Tout','Vente','Recherche','À donner'].map(f=>(
              <button key={f} onClick={()=>setFilterType(f)} style={{padding:'3px 8px',borderRadius:5,fontSize:'0.6875rem',fontWeight:filterType===f?700:400,cursor:'pointer',border:'none',backgroundColor:filterType===f?'white':'transparent',color:filterType===f?'#111827':'#6B7280',boxShadow:filterType===f?'0 1px 2px rgba(0,0,0,0.08)':'none'}}>{f}</button>
            ))}
          </div>
          <div style={{position:'relative'}}>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{...inp,width:'auto',appearance:'none',paddingRight:28,paddingLeft:10,padding:'6px 28px 6px 10px',fontSize:'0.75rem',color:filterCat?'#111827':'#9CA3AF'}}>
              <option value="">Toutes catégories</option>
              {refs.categorie.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',width:12,height:12,pointerEvents:'none',color:'#9CA3AF'}}/>
          </div>
          <div style={{display:'flex',gap:1,backgroundColor:'#F3F4F6',borderRadius:6,padding:2}}>
            {[['list',<LayoutList style={{width:15,height:15}}/>],['grid',<LayoutGrid style={{width:15,height:15}}/>]].map(([m,icon])=>(
              <button key={m} onClick={()=>setViewMode(m)} style={{padding:'4px 7px',borderRadius:4,border:'none',cursor:'pointer',backgroundColor:viewMode===m?'white':'transparent',color:viewMode===m?'#111827':'#9CA3AF',boxShadow:viewMode===m?'0 1px 2px rgba(0,0,0,0.08)':'none',display:'flex',alignItems:'center'}}>{icon}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Mes annonces (dropdown overlay) ── */}
      {isAuthenticated && (
        <div style={{position:'relative',zIndex:50}}>
          <button onClick={()=>setShowMyAnnonces(v=>!v)} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',backgroundColor:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:showMyAnnonces?'10px 10px 0 0':10,cursor:'pointer',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:7}}>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#1D4ED8',textTransform:'uppercase',letterSpacing:'0.06em'}}>Mes annonces</span>
              {myAnnonces.length > 0 && <span style={{backgroundColor:'#DBEAFE',color:'#1D4ED8',fontSize:'0.625rem',fontWeight:700,padding:'1px 6px',borderRadius:9999}}>{myAnnonces.length}</span>}
            </div>
            <span style={{fontSize:'0.75rem',color:'#3B82F6',fontWeight:600}}>{showMyAnnonces ? '▲ Réduire' : '▼ Voir'}</span>
          </button>
          {showMyAnnonces && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,border:'1px solid #BFDBFE',borderTop:'none',borderRadius:'0 0 10px 10px',backgroundColor:'#F8FAFF',padding:'10px 12px',display:'flex',flexDirection:'column',gap:6,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',maxHeight:320,overflowY:'auto'}}>
              {myAnnonces.map(a => {
                const st = STATUS_STYLES[a.statut] || STATUS_STYLES.draft;
                const dl = daysLeft(a.expires_at);
                return (
                  <div key={a.id} style={{display:'flex',flexDirection:'column',gap:6,padding:'9px 11px',backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer'}} onClick={()=>openDetail(a.id)}>
                    {/* Ligne 1 : titre + badge */}
                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:'0.8125rem',color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.titre}</div>
                        <div style={{fontSize:'0.6875rem',color:'#9CA3AF',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {a.type}{a.categorie ? ` · ${a.categorie}` : ''}{dl !== null && a.statut==='published' ? ` · ${dl}j` : ''}
                        </div>
                      </div>
                      <span style={{backgroundColor:st.bg,color:st.c,fontSize:'0.6rem',fontWeight:700,padding:'2px 7px',borderRadius:20,flexShrink:0,whiteSpace:'nowrap'}}>{st.label}</span>
                    </div>
                    {/* Ligne 2 : boutons */}
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}} onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>{e.stopPropagation();openEdit(a);}} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 7px',borderRadius:5,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.6875rem',color:'#374151'}}><Edit2 style={{width:10,height:10}}/> Modifier</button>
                      {a.statut==='published' && <button onClick={e=>{e.stopPropagation();setRenewDuree(a.duree_mois||1);setRenewModal({id:a.id});}} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 7px',borderRadius:5,border:'1px solid #BBF7D0',backgroundColor:'#F0FDF4',cursor:'pointer',fontSize:'0.6875rem',color:'#15803D'}}><RefreshCw style={{width:10,height:10}}/> Prolonger</button>}
                      {a.statut==='published' && <button onClick={e=>{e.stopPropagation();handleWithdraw(a.id);}} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 7px',borderRadius:5,border:'1px solid #FED7AA',backgroundColor:'#FFF7ED',cursor:'pointer',fontSize:'0.6875rem',color:'#C2410C'}}><EyeOff style={{width:10,height:10}}/> Retirer</button>}
                      <button onClick={e=>{e.stopPropagation();handleDelete(a.id);}} style={{display:'inline-flex',alignItems:'center',padding:'3px 6px',borderRadius:5,border:'1px solid #FECACA',backgroundColor:'#FEF2F2',cursor:'pointer',color:'#DC2626'}}><Trash2 style={{width:10,height:10}}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Liste/Grille ── */}
      {filtered.length === 0 ? (
        <div style={{backgroundColor:'white',border:'1px dashed #E5E7EB',borderRadius:10,padding:'3rem',textAlign:'center'}}>
          <Store style={{width:32,height:32,color:'#D1D5DB',margin:'0 auto 10px',display:'block'}}/>
          <p style={{color:'#9CA3AF',fontSize:'0.875rem',margin:0}}>Aucune annonce{search ? ` pour "${search}"` : ''}</p>
        </div>
      ) : viewMode === 'list' ? (
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {filtered.map(a => {
            const ts = TYPE_STYLES[a.type] || TYPE_STYLES.Vente;
            return (
              <div key={a.id} onClick={()=>openDetail(a.id)} style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:10,display:'flex',alignItems:'stretch',overflow:'hidden',boxShadow:'0 1px 2px rgba(0,0,0,0.04)',cursor:'pointer',transition:'box-shadow 0.15s'}}>
                <AnnonceImage type={a.type} seed={a.id} size="list" images={a.images}/>
                <div style={{flex:1,minWidth:0,padding:'8px 10px',display:'flex',alignItems:'center',gap:8}}>
                  <span style={{backgroundColor:ts.bg,color:ts.c,border:`1px solid ${ts.border}`,fontSize:'0.6rem',fontWeight:700,padding:'1px 6px',borderRadius:20,whiteSpace:'nowrap',flexShrink:0}}>{a.type}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:'0.8125rem',color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.titre}</div>
                    <div style={{fontSize:'0.6875rem',color:'#9CA3AF',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {[a.marque,a.modele].filter(Boolean).join(' ')||a.categorie||''}{a.localisation ? ` · ${a.localisation}` : ''}
                    </div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    {a.prix ? <div style={{fontSize:'0.875rem',fontWeight:800,color:'#111827'}}>{Number(a.prix).toLocaleString('fr-CH')} <span style={{fontSize:'0.6875rem',fontWeight:500,color:'#9CA3AF'}}>{a.devise||'CHF'}</span></div> : <div style={{fontSize:'0.6875rem',color:'#9CA3AF',fontStyle:'italic'}}>—</div>}
                    <div style={{fontFamily:'monospace',fontSize:'0.6875rem',fontWeight:700,color:'#6B7280'}}>{a.auteur_indicatif}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(3,1fr)',gap:12}}>
          {filtered.map(a => {
            const ts = TYPE_STYLES[a.type] || TYPE_STYLES.Vente;
            return (
              <div key={a.id} onClick={()=>openDetail(a.id)} style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',display:'flex',flexDirection:'column',cursor:'pointer',transition:'box-shadow 0.15s'}}>
                <AnnonceImage type={a.type} seed={a.id} size="grid" images={a.images}/>
                <div style={{padding:'8px 10px',flex:1,display:'flex',flexDirection:'column',gap:4}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:4}}>
                    <span style={{backgroundColor:ts.bg,color:ts.c,border:`1px solid ${ts.border}`,fontSize:'0.55rem',fontWeight:700,padding:'1px 5px',borderRadius:20,whiteSpace:'nowrap'}}>{a.type}</span>
                    <span style={{fontFamily:'monospace',fontSize:'0.6rem',fontWeight:700,color:'#9CA3AF'}}>{a.auteur_indicatif}</span>
                  </div>
                  <div style={{fontWeight:700,fontSize:'0.8125rem',color:'#111827',lineHeight:1.3,overflow:'hidden',display:'-webkit-box',WebkitBoxOrient:'vertical',WebkitLineClamp:2}}>{a.titre}</div>
                  {(a.marque||a.modele) && <div style={{fontSize:'0.6875rem',color:'#6B7280',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{[a.marque,a.modele].filter(Boolean).join(' · ')}</div>}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'auto',paddingTop:4,borderTop:'1px solid #F3F4F6'}}>
                    {a.prix ? <span style={{fontSize:'0.8125rem',fontWeight:800,color:'#111827'}}>{Number(a.prix).toLocaleString('fr-CH')} <span style={{fontSize:'0.6rem',color:'#9CA3AF'}}>{a.devise||'CHF'}</span></span> : <span style={{fontSize:'0.6875rem',color:'#9CA3AF',fontStyle:'italic'}}>—</span>}
                    {a.localisation && <span style={{fontSize:'0.6rem',color:'#9CA3AF',display:'flex',alignItems:'center',gap:2}}><MapPin style={{width:8,height:8}}/>{a.localisation}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Formulaire modal responsive ── */}
      {showForm && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.55)',display:'flex',alignItems:isMobile?'flex-end':'center',justifyContent:'center',zIndex:400,padding:isMobile?0:'16px'}} onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div style={{backgroundColor:'white',width:'100%',maxWidth:isMobile?'100%':1060,borderRadius:isMobile?'16px 16px 0 0':14,boxShadow:'0 24px 64px rgba(0,0,0,0.22)',display:'flex',flexDirection:'column',maxHeight:isMobile?'92vh':'calc(100vh - 32px)',overflow:'hidden'}}>

            {/* Header */}
            <div style={{padding:isMobile?'14px 18px':'16px 24px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div>
                <div style={{fontWeight:800,fontSize:'1rem',color:'#111827'}}>{editTarget?"Modifier l'annonce":'Déposer une annonce'}</div>
                {!isMobile && <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>Tous les champs marqués * sont obligatoires</div>}
              </div>
              <button onClick={()=>setShowForm(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex',borderRadius:6}}><X style={{width:18,height:18}}/></button>
            </div>

            <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden'}}>

              {/* Type */}
              <div style={{padding:isMobile?'10px 18px':'12px 24px',borderBottom:'1px solid #F9FAFB',flexShrink:0,display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:'0.75rem',fontWeight:700,color:'#374151',whiteSpace:'nowrap'}}>Type</span>
                <div style={{display:'flex',gap:6,flex:1,flexWrap:'wrap'}}>
                  {['Vente','Recherche','À donner'].map(t => {
                    const ts = TYPE_STYLES[t]; const active = form.type===t;
                    return <button key={t} type="button" onClick={()=>setForm({...form,type:t})} style={{padding:'5px 14px',borderRadius:20,fontSize:'0.8125rem',fontWeight:700,cursor:'pointer',border:`1.5px solid ${active?ts.border:'#E5E7EB'}`,backgroundColor:active?ts.bg:'white',color:active?ts.c:'#9CA3AF'}}>{t}</button>;
                  })}
                </div>
              </div>

              {/* Corps — 2 colonnes desktop, 1 colonne mobile scrollable */}
              <div style={isMobile ? {display:'flex',flexDirection:'column',flex:1,overflowY:'auto',padding:'14px 18px',gap:13} : {display:'grid',gridTemplateColumns:'1fr 1fr',flex:1,overflow:'hidden'}}>

                {/* Bloc gauche (ou top sur mobile) */}
                <div style={isMobile ? {display:'flex',flexDirection:'column',gap:13} : {padding:'16px 20px 16px 24px',display:'flex',flexDirection:'column',gap:11,borderRight:'1px solid #F3F4F6',overflowY:'auto'}}>

                  <div>
                    <label style={lbl}>Titre *</label>
                    <input required style={inp} value={form.titre} onChange={e=>setForm({...form,titre:e.target.value})} placeholder={form.type==='Vente'?'Ex: Icom IC-7300 — excellent état':'Ex: Recherche alimentation 30A'}/>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div>
                      <label style={lbl}>Catégorie *</label>
                      <div style={{position:'relative'}}>
                        <select required style={{...inp,appearance:'none',paddingRight:24}} value={form.categorie} onChange={e=>setForm({...form,categorie:e.target.value})}>
                          <option value="">Choisir…</option>
                          {refs.categorie.map(c=><option key={c} value={c}>{c}</option>)}
                        </select>
                        <ChevronDown style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',width:12,height:12,pointerEvents:'none',color:'#9CA3AF'}}/>
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>État{form.type==='Vente'?' *':''}</label>
                      <div style={{position:'relative'}}>
                        <select style={{...inp,appearance:'none',paddingRight:24}} required={form.type==='Vente'} value={form.etat} onChange={e=>setForm({...form,etat:e.target.value})}>
                          <option value="">Choisir…</option>
                          {refs.etat.map(e=><option key={e} value={e}>{e}</option>)}
                        </select>
                        <ChevronDown style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',width:12,height:12,pointerEvents:'none',color:'#9CA3AF'}}/>
                      </div>
                    </div>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div>
                      <label style={lbl}>Marque</label>
                      <div style={{position:'relative'}}>
                        <select style={{...inp,appearance:'none',paddingRight:24}} value={form.marque} onChange={e=>setForm({...form,marque:e.target.value})}>
                          <option value="">Choisir…</option>
                          {refs.marque.map(m=><option key={m} value={m}>{m}</option>)}
                        </select>
                        <ChevronDown style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',width:12,height:12,pointerEvents:'none',color:'#9CA3AF'}}/>
                      </div>
                      {form.marque==='Andere / Autre' && <input style={{...inp,marginTop:5}} placeholder="Préciser la marque…" value={form.marqueLibre||''} onChange={e=>setForm({...form,marqueLibre:e.target.value})}/>}
                    </div>
                    <div>
                      <label style={lbl}>Modèle / Réf.</label>
                      <input style={inp} value={form.modele} onChange={e=>setForm({...form,modele:e.target.value})} placeholder="IC-7300, FT-991A…"/>
                    </div>
                  </div>

                  {form.type !== 'À donner' && (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 80px',gap:8}}>
                      <div>
                        <label style={lbl}>Prix{form.type==='Vente'?' *':''}</label>
                        <input type="number" min="0" step="0.01" required={form.type==='Vente'} style={inp} value={form.prix} onChange={e=>setForm({...form,prix:e.target.value})} placeholder="0.00"/>
                      </div>
                      <div>
                        <label style={lbl}>Devise</label>
                        <div style={{position:'relative'}}>
                          <select style={{...inp,appearance:'none',paddingRight:18}} value={form.devise} onChange={e=>setForm({...form,devise:e.target.value})}>
                            {refs.devise.map(d=><option key={d} value={d}>{d}</option>)}
                          </select>
                          <ChevronDown style={{position:'absolute',right:5,top:'50%',transform:'translateY(-50%)',width:11,height:11,pointerEvents:'none',color:'#9CA3AF'}}/>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label style={lbl}>Localisation</label>
                    <input style={inp} value={form.localisation} onChange={e=>setForm({...form,localisation:e.target.value})} placeholder="Genève, Vaud, Zurich…"/>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div>
                      <label style={lbl}>Email de contact <span style={{fontWeight:600,color:'#DC2626'}}>*</span></label>
                      <input type="email" style={inp} value={form.contact_email} onChange={e=>setForm({...form,contact_email:e.target.value})} placeholder="email@exemple.com"/>
                    </div>
                    <div>
                      <label style={lbl}>Téléphone <span style={{fontWeight:600,color:'#DC2626'}}>*</span></label>
                      <input type="tel" style={inp} value={form.contact_telephone} onChange={e=>setForm({...form,contact_telephone:e.target.value})} placeholder="+41 79 000 00 00"/>
                    </div>
                  </div>
                  <p style={{fontSize:'0.75rem',color:'#6B7280',margin:'4px 0 0',fontStyle:'italic'}}>
                    * Au moins un moyen de contact est requis (email ou téléphone)
                  </p>

                </div>

                {/* Bloc droit (ou suite sur mobile) */}
                <div style={isMobile ? {display:'flex',flexDirection:'column',gap:13} : {padding:'16px 24px 16px 20px',display:'flex',flexDirection:'column',gap:11,overflowY:'auto'}}>

                  <div style={{display:'flex',flexDirection:'column'}}>
                    <label style={lbl}>Description *</label>
                    <textarea required style={{...inp,minHeight:isMobile?90:120,resize:'none',lineHeight:1.55}} value={form.contenu} onChange={e=>setForm({...form,contenu:e.target.value})} placeholder="Accessoires inclus, état détaillé, conditions de vente…"/>
                  </div>

                  {/* Photos */}
                  <div>
                    <label style={{...lbl,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <span>Photos</span>
                      <span style={{fontWeight:400,color:'#9CA3AF',fontSize:'0.6875rem'}}>max. 3 · JPG PNG WebP · 5 Mo</span>
                    </label>
                    <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      {keptPhotos.map((url,i)=>(
                        <div key={`k${i}`} style={{position:'relative',width:68,height:68,borderRadius:8,overflow:'hidden',border:'1px solid #E5E7EB',flexShrink:0}}>
                          <img src={`${API_BASE}${url}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                          <button type="button" onClick={()=>removeKept(url)} style={{position:'absolute',top:2,right:2,width:17,height:17,borderRadius:'50%',background:'rgba(0,0,0,0.65)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}><X style={{width:9,height:9,color:'white'}}/></button>
                        </div>
                      ))}
                      {newPhotos.map((f,i)=>(
                        <div key={`n${i}`} style={{position:'relative',width:68,height:68,borderRadius:8,overflow:'hidden',border:'1px solid #E5E7EB',flexShrink:0}}>
                          <img src={URL.createObjectURL(f)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                          <button type="button" onClick={()=>removeNew(i)} style={{position:'absolute',top:2,right:2,width:17,height:17,borderRadius:'50%',background:'rgba(0,0,0,0.65)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}><X style={{width:9,height:9,color:'white'}}/></button>
                        </div>
                      ))}
                      {photoCount < 3 && (
                        <button type="button" onClick={()=>fileInputRef.current?.click()} style={{width:68,height:68,borderRadius:8,border:'2px dashed #D1D5DB',background:'#F9FAFB',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:3,color:'#9CA3AF',fontSize:'0.625rem',fontWeight:600}}>
                          <Camera style={{width:16,height:16}}/>Ajouter
                        </button>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{display:'none'}} onChange={handlePhotoAdd}/>
                    </div>
                  </div>

                  {/* Durée */}
                  <div>
                    <label style={lbl}>Durée de publication</label>
                    <div style={{display:'flex',gap:7}}>
                      {[{v:1,label:'1 mois',desc:'30 jours'},{v:3,label:'3 mois',desc:'90 jours'}].map(({v,label,desc})=>{
                        const active = Number(form.duree_mois)===v;
                        return (
                          <button key={v} type="button" onClick={()=>setForm({...form,duree_mois:v})} style={{flex:1,padding:'9px 10px',borderRadius:8,border:`1.5px solid ${active?'#6366F1':'#E5E7EB'}`,backgroundColor:active?'#EEF2FF':'white',cursor:'pointer',textAlign:'left'}}>
                            <div style={{fontWeight:700,fontSize:'0.875rem',color:active?'#4F46E5':'#374151'}}>{label}</div>
                            <div style={{fontSize:'0.75rem',color:active?'#6366F1':'#9CA3AF'}}>{desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{backgroundColor:'#FEF9C3',border:'1px solid #FDE68A',borderRadius:7,padding:'8px 12px',display:'flex',alignItems:'center',gap:7}}>
                    <AlertCircle style={{width:13,height:13,color:'#D97706',flexShrink:0}}/>
                    <p style={{fontSize:'0.75rem',color:'#92400E',margin:0,lineHeight:1.4}}>Votre annonce sera <strong>révisée par le comité</strong> avant publication · visible <strong>{form.duree_mois===3?'3 mois':'1 mois'}</strong> · renouvelable avec validation</p>
                  </div>

                </div>
              </div>

              {/* Footer */}
              <div style={{padding:isMobile?'12px 18px':'12px 24px',borderTop:'1px solid #F3F4F6',display:'flex',justifyContent:'flex-end',gap:8,flexShrink:0,backgroundColor:'white'}}>
                <button type="button" onClick={()=>setShowForm(false)} style={{padding:'9px 18px',borderRadius:8,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151',fontWeight:500}}>Annuler</button>
                <button type="submit" disabled={submitting} style={{flex:isMobile?1:undefined,padding:'9px 20px',borderRadius:8,backgroundColor:'#111827',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700,opacity:submitting?0.7:1}}>
                  {submitting?'Envoi…':editTarget?'Enregistrer':'Soumettre l\'annonce'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal confirmation ── */}
      {confirm && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setConfirm(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px 28px',maxWidth:400,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:10}}>Confirmer</div>
            <p style={{color:'#6B7280',fontSize:'0.9rem',margin:'0 0 24px',lineHeight:1.6}}>{confirm.message}</p>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setConfirm(null)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
              <button onClick={()=>{confirm.onConfirm();setConfirm(null);}} style={{padding:'8px 16px',borderRadius:7,backgroundColor:confirm.confirmColor||'#DC2626',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700}}>{confirm.confirmLabel||'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal prolongation ── */}
      {renewModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setRenewModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px 28px',maxWidth:380,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:6}}>Prolonger l'annonce</div>
            <p style={{color:'#6B7280',fontSize:'0.875rem',margin:'0 0 18px',lineHeight:1.5}}>Choisissez la durée de prolongation. L'expiration sera calculée depuis aujourd'hui.</p>
            <div style={{display:'flex',gap:10,marginBottom:22}}>
              {[{v:1,label:'1 mois',desc:'30 jours'},{v:3,label:'3 mois',desc:'90 jours'}].map(({v,label,desc}) => {
                const active = renewDuree === v;
                return (
                  <button key={v} type="button" onClick={()=>setRenewDuree(v)} style={{flex:1,padding:'12px',borderRadius:8,border:`2px solid ${active?'#6366F1':'#E5E7EB'}`,backgroundColor:active?'#EEF2FF':'white',cursor:'pointer',textAlign:'left'}}>
                    <div style={{fontWeight:700,fontSize:'0.9375rem',color:active?'#4F46E5':'#374151'}}>{label}</div>
                    <div style={{fontSize:'0.75rem',color:active?'#6366F1':'#9CA3AF',marginTop:2}}>{desc}</div>
                  </button>
                );
              })}
            </div>
            <div style={{backgroundColor:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:7,padding:'9px 13px',fontSize:'0.8125rem',color:'#166534',marginBottom:20}}>
              Nouvelle expiration : <strong>{format(addMonths(new Date(), renewDuree), 'dd MMM yyyy', {locale:fr})}</strong>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setRenewModal(null)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
              <button onClick={handleRenewConfirm} style={{padding:'8px 16px',borderRadius:7,backgroundColor:'#15803D',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700,display:'flex',alignItems:'center',gap:6}}><RefreshCw style={{width:13,height:13}}/> Prolonger</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bandeau connexion requise ── */}
      {showLoginBanner && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500,padding:16}} onClick={()=>setShowLoginBanner(false)}>
          <div style={{backgroundColor:'white',borderRadius:14,width:'100%',maxWidth:380,padding:'28px 24px',boxShadow:'0 8px 40px rgba(0,0,0,0.2)',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{width:48,height:48,borderRadius:'50%',backgroundColor:'#F3F4F6',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
              <Lock style={{width:22,height:22,color:'#374151'}}/>
            </div>
            <h3 style={{fontWeight:800,fontSize:'1.0625rem',color:'#111827',margin:'0 0 8px'}}>Connexion requise</h3>
            <p style={{fontSize:'0.875rem',color:'#6B7280',margin:'0 0 20px',lineHeight:1.5}}>Vous devez être connecté pour déposer une annonce. Créez un compte ou connectez-vous pour accéder à cette fonctionnalité.</p>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>setShowLoginBanner(false)} style={{padding:'9px 18px',borderRadius:8,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151',fontWeight:600}}>Annuler</button>
              <button onClick={()=>navigate('/auth')} style={{padding:'9px 18px',borderRadius:8,backgroundColor:'#111827',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700}}>Se connecter</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
};

export default AnnoncesPage;
